import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { Brand, SectorAverages } from '@fametrue/shared';
import { apiRequest } from '../lib/api';

export interface BrandListItem {
  slug: string;
  name: string;
  sector: string;
  sectorKey: string;
  matrixLabel: string;
  fameIntegrity: number | null;
  fameRiskLevel: number;
}

export interface SectorBrandRow {
  slug: string;
  name: string;
  webScore: number;
  organicScore: number | null;
  fameIndex: number | null;
  fameIndexLabel: string;
  fameIntegrity: number | null;
  fameRiskLevel: number;
  fameRiskLabel: string;
  matrixLabel: string;
  isOwn: boolean;
}

export interface SectorSummary {
  sectorKey: string;
  sector: string;
  brandCount: number;
  averages: SectorAverages | null;
  benchmarkAvailable: boolean;
  brands: SectorBrandRow[];
}

export interface BrandDetail {
  brand: Brand;
  importedAt: string;
}

/** Elenco dei brand accessibili: alimenta il selettore in top bar. */
export function useBrands(): UseQueryResult<BrandListItem[]> {
  return useQuery({
    queryKey: ['brands'],
    queryFn: async () => (await apiRequest<{ brands: BrandListItem[] }>('/api/brands')).brands,
    staleTime: 5 * 60 * 1000,
  });
}

export function useBrand(slug: string | undefined): UseQueryResult<BrandDetail> {
  return useQuery({
    queryKey: ['brand', slug],
    queryFn: () => apiRequest<BrandDetail>(`/api/brands/${slug ?? ''}`),
    enabled: Boolean(slug),
    retry: false,
  });
}

export function useSector(sectorKey: string | undefined): UseQueryResult<SectorSummary> {
  return useQuery({
    queryKey: ['sector', sectorKey],
    queryFn: () => apiRequest<SectorSummary>(`/api/sectors/${sectorKey ?? ''}`),
    enabled: Boolean(sectorKey),
    retry: false,
  });
}

/**
 * Raggruppa i brand per verticale, come fa il selettore del riferimento.
 * L'ordine dei verticali segue il nome leggibile, i brand il proprio nome.
 */
export function groupBySector(
  brands: BrandListItem[],
): { sectorKey: string; sector: string; brands: BrandListItem[] }[] {
  const groups = new Map<string, { sectorKey: string; sector: string; brands: BrandListItem[] }>();

  for (const brand of brands) {
    const existing = groups.get(brand.sectorKey);
    if (existing) existing.brands.push(brand);
    else
      groups.set(brand.sectorKey, {
        sectorKey: brand.sectorKey,
        sector: brand.sector,
        brands: [brand],
      });
  }

  return [...groups.values()].sort((a, b) => a.sector.localeCompare(b.sector));
}
